"use client";

import { useSearchFocus } from "@/app/context";
import { useOnClickOutside } from "@/app/hooks/useOnClickOutside";
import type { FlightPlaces } from "@/types/flights";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { DestinationFieldDropdown } from "./DestinationFieldDropdown";
import { useTranslations } from "next-intl";

type Props = {
  value: string;
  label: string;
  options: FlightPlaces[];
  onChange: Dispatch<SetStateAction<string>>;
  onClick: (flight: FlightPlaces) => void;
  otherValue: string;
};

export const DestinationDropdown = ({
  value,
  label,
  options,
  onChange,
  onClick,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { registerInput } = useSearchFocus();
  const t = useTranslations();

  const [isOpen, setOpen] = useState(false);
  const ref = useOnClickOutside<HTMLDivElement>(() => setOpen(false));

  const handleClick = ({ name, iataCode }: FlightPlaces) => {
    setOpen((prev) => !prev);
    onClick({ name, iataCode });
  };

  useEffect(() => {
    registerInput(label, inputRef);
  }, [label, registerInput]);

  return (
    <div className="flex-grow relative" ref={ref}>
      <label
        className="flex items-center rounded-lg border border-sleap-gray-400 md:border-none"
        onClick={() => setOpen(true)}
      >
        <span className="font-medium text-inter text-base text-sleap-gray-950 dark:text-white pl-4">
          {label}
        </span>
        <input
          type="text"
          ref={inputRef}
          className="px-2 py-4 min-h-14 outline-none w-full rounded-lg text-inter text-base bg-transparent"
          value={value}
          placeholder={t("HomePageFlights.countryCityAirport")}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      {isOpen && (
        <DestinationFieldDropdown options={options} onClick={handleClick} />
      )}
    </div>
  );
};
